import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http'
import { inject, Injectable, Injector, signal } from '@angular/core'
import { Router } from '@angular/router'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import type { NotificationChannel } from '@app/modules/database/models/resend-confirmation-pricing'
import { IRunningJob } from '@app/modules/shared/header/running-jobs/model/running-jobs.interface'
import { BetaFeature } from '@app/services/beta-features.service'
import { PosthogService } from '@app/services/posthog.service'
import { AuthData, Credentials, ForgetPasswordResponse } from '@app/v2/auth/types/auth-data.type'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { SystemOptions } from '@app/v2/shared/components/navbar/navbar.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { TransformedV3User, UserTransformerService } from '@app/v2/shared/transformers/user-transformer.service'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { environment } from '@environments/environment'
import { Workspace } from '@workspaces/interfaces/workspace.interface'
import { PlanPrice } from '@workspaces/models/plan-price.interface'
import { BehaviorSubject, from, Observable, of } from 'rxjs'
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators'

/**
 * Defines an organization profile - legal info
 */
export interface LegalInfo {
  /** Company address */
  company_address?: string
  /** Company description */
  company_description?: string
  /** Company email */
  company_email: string
  /** Company name */
  company_name: string
  /** Company phone */
  company_phone?: string
  /** Company type */
  company_type: number
  /** Country */
  country: string
  /** Created at */
  created_at?: string
  /** Company metadata */
  data?: any
  /** Company logo */
  company_logo?: string
  /** Legal info id */
  id?: number
  /** Updated at */
  updated_at?: string
  /** Workspace id */
  workspace_id: any
  /** Industry_sector */
  industry_sector_id?: number
}

/**
 * @type EmailExistsResponse holds the response of the email existance check
 */
export type EmailExistsResponse = Response<{ email_exists: boolean }>
/**
 * @type AuthDataResponse holds the response of the authentication
 */
export type AuthDataResponse = Response<AuthData>
/**
 * @type StatusResponse holds the response of status messages
 */
export type StatusResponse = Response<any[]>
/**
 * @type disableAccountResponse holds the response of the disable account
 */
export type disableAccountResponse = Response<{ full_name: string; case_number: number }>

/**
 * Service that handles authentication, authorization and related functionality
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public flags: BetaFeature[] = []
  /**
   * Current user token
   */
  private _token: string | null = null
  private _tokenExpiresAtSec: number | null = null
  private _tokenInFlight$?: Observable<string | null>
  /**
   * Current application version
   */
  public _version = environment.appVersion
  /**
   * Emits the current user when it changes
   */
  public userChange: BehaviorSubject<any> = new BehaviorSubject(this.user)
  /**
   * Emits the current workspace when it changes
   */
  public currentWorkspaceChange: BehaviorSubject<Workspace> = new BehaviorSubject(this.currentWorkspace)
  /**
   * Emits the current parent workspace when it changes
   */
  public currentParentWorkspaceChange: BehaviorSubject<Workspace> = new BehaviorSubject(this.currentParentWorkspace)
  /**
   * Whether the terms of services are displayed
   */
  public displayTerms$: BehaviorSubject<boolean> = new BehaviorSubject(false)
  /**
   * Whether the privacy policy is displayed
   */
  public displayPrivacy$: BehaviorSubject<boolean> = new BehaviorSubject(false)
  /**
   * Whether the available plans changed
   */
  public plansChange: BehaviorSubject<any> = new BehaviorSubject(this.plans)
  /**
   * Whether the role of current user in current workspace has changed
   */
  public userRoleInCurrentWorksapce: BehaviorSubject<any> = new BehaviorSubject(null)

  /**
   * BehaviorSubject for storing running jobs.
   * @private
   */
  private runningJobsSource = new BehaviorSubject<IRunningJob[]>([])
  /**
   * Observable for broadcasting running job changes.
   * @public
   */
  runningJobs$ = this.runningJobsSource.asObservable()
  private _organizationList = signal<Workspace[]>([])
  public readonly workspaceList = this._organizationList.asReadonly()
  private _router = inject(Router)
  private _injector = inject(Injector)
  private _alertService = inject(AlertService)
  private _organizationService = inject(OrganizationService)
  private _currentUser: TransformedV3User = null
  private _currentWorkspace: Workspace = null
  private _currentParentWorkspace: Workspace = null
  private _signOutInProgress = false

  /**
   * gets the current user from memory (no browser storage)
   */
  public get user(): TransformedV3User {
    return this._currentUser
  }

  /**
   * Sets the current user in memory and calls userChange
   * @param value User
   */
  public set user(value: AuthData) {
    if (!value) {
      this._currentUser = null
      this._posthogService.resetSession()
      this.userChange.next(null)
      return
    }
    const transformedUser = this._userTransformer.transformV4ToV3(value)
    this._currentUser = transformedUser
    this._posthogService.setUser(transformedUser)
    this.userChange.next(transformedUser)
  }

  /**
   * Sets the plans available in local storage
   * @param value Plans
   */
  public set plans(value) {
    if (value === null) {
      localStorage.removeItem('plans')
    } else {
      localStorage.setItem('plans', JSON.stringify(value))
    }
    this.plansChange.next(value)
  }

  /**
   * Gets the plans available from local storage
   */
  public get plans() {
    const plans = localStorage.getItem('plans')
    return plans ? JSON.parse(plans) : []
  }

  /**
   * Gets the current available workspaces from local storage
   */
  public get workspaces() {
    if (this._organizationList().length === 0) {
      const workspaces = localStorage.getItem('workspaces')
      this._organizationList.set(workspaces ? JSON.parse(workspaces) : [])
    }
    return this._organizationList()
  }

  /**
   * Sets the current available workspaces in local storage
   */
  public set workspaces(value: Workspace[]) {
    this._organizationList.set(value)
    if (value.length === 0) {
      localStorage.removeItem('workspaces')
    } else {
      localStorage.setItem('workspaces', JSON.stringify(value))
    }
  }

  /**
   * Sets the current workspace in local storage and emits currentWorkspaceChange
   */
  public set currentWorkspace(value: Workspace) {
    if (value === null) {
      sessionStorage.removeItem('current-workspace')
    } else {
      sessionStorage.setItem('current-workspace', JSON.stringify(value))
    }
    this._currentWorkspace = value
    this.currentWorkspaceChange.next(value)
  }

  /**
   * Gets the current workspace from local storage
   */
  public get currentWorkspace(): Workspace {
    if (this._currentWorkspace) return this._currentWorkspace
    const workspace = sessionStorage.getItem('current-workspace')
    return workspace ? JSON.parse(workspace) : null
  }

  /**
   * Determines whether the user can edit workspace organization info
   */
  public get canEditOrganization(): boolean {
    return !!this.currentParentWorkspace && this.currentParentWorkspace.is_auth_owner && this.currentParentWorkspace.role_id === 1
  }
  /**
   * Sets the current parent workspace in local storage and emits currentParentWorkspaceChange
   */
  public set currentParentWorkspace(value: Workspace) {
    if (value === null) {
      sessionStorage.removeItem('current-parent-workspace')
    } else {
      sessionStorage.setItem('current-parent-workspace', JSON.stringify(value))
    }
    this._currentParentWorkspace = value
    this.currentParentWorkspaceChange.next(value)
  }

  /**
   * Gets the current parent workspace from local storage
   */
  public get currentParentWorkspace(): Workspace {
    if (this._currentParentWorkspace) return this._currentParentWorkspace
    const workspace = sessionStorage.getItem('current-parent-workspace')
    return workspace ? JSON.parse(workspace) : null
  }

  /**
   * Creates the service & injects it's dependencies
   * @param http HttpClient
   * @param _router Router
   */
  constructor(private http: HttpClient, private _userTransformer: UserTransformerService, private _posthogService: PosthogService) {
    this._currentUser = this.user
  }

  /**
   * Checks if a step-up is requested
   * @returns True if a step-up is requested, false otherwise
   */
  public isStepUpRequested(): boolean {
    try {
      return new URLSearchParams(window.location.search).get('step_up') === 'planning'
    } catch {
      return false
    }
  }

  /**
   * Clears the cross-app redirect state
   */
  public clearCrossAppRedirectState(): void {
    this.clearRedirectUrl()
    try {
      const url = new URL(window.location.href)
      let changed = false
      ;['redirect', 'redirect_url', 'mt_token'].forEach((key) => {
        if (!url.searchParams.has(key)) return
        url.searchParams.delete(key)
        changed = true
      })
      const nestedReturnTo = url.searchParams.get('return_to')
      if (nestedReturnTo && !this._isPlanReturnUrl(nestedReturnTo)) {
        url.searchParams.delete('return_to')
        changed = true
      }
      if (changed) {
        window.history.replaceState({}, '', url.pathname + url.search + url.hash)
      }
    } catch {}
  }

  /**
   * Starts server-side OAuth Code+PKCE (BFF).
   * No PKCE secrets or refresh tokens are stored in the browser.
   */
  beginLogin(intendedUrl?: string, requireStepUp = false): void {
    // Prevent cross-user data bleed: if we are about to send the user to the IdP,
    // clear persisted app state first (preserve only UI prefs).
    const preserved: Record<string, string> = {}
    ;['theme', 'langCode'].forEach((k) => {
      try {
        const v = localStorage.getItem(k)
        if (v !== null) preserved[k] = v
      } catch {}
    })
    try {
      localStorage.clear()
    } catch {}
    try {
      sessionStorage.clear()
    } catch {}
    Object.entries(preserved).forEach(([k, v]) => {
      try {
        localStorage.setItem(k, v)
      } catch {}
    })

    const returnTo = this._resolvePlanReturnTo(intendedUrl)

    const base = `${environment.baseContactlessApi}v3/auth/bff/login`
    const url = `${base}` + `?return_to=${encodeURIComponent(returnTo)}` + (requireStepUp ? `&step_up=planning` : ``)

    window.location.replace(url)
  }

  // OAuth code exchange happens server-side in BFF mode.

  /**
   * Gets the current application version
   * @returns Observable
   */
  getLiveVersion(autoGenerated = Math.random().toString().replace('0.', '')): Observable<any> {
    return this.http.get(`${environment.baseUrl}version?version=${this._version}&current=${autoGenerated}`).pipe(tap((data) => (this._version = data['version'])))
  }

  /**
   * Gets the device name by detecting platform, browser and device type
   * @returns A string containing device type (Mobile/Desktop), platform and browser name
   * @example "Desktop-MacIntel-Chrome" or "Mobile-iPhone-Safari"
   */
  private _getDeviceName(): string {
    const platform = navigator.platform || 'UnknownPlatform'
    const userAgent = navigator.userAgent.toLowerCase()

    const isMobile = /mobile|android|iphone|ipad|phone/i.test(userAgent)
    const browser = /chrome/.test(userAgent)
      ? 'Chrome'
      : /firefox/.test(userAgent)
      ? 'Firefox'
      : /safari/.test(userAgent) && !/chrome/.test(userAgent)
      ? 'Safari'
      : 'UnknownBrowser'

    return `${isMobile ? 'Mobile' : 'Desktop'}-${platform}-${browser}`
  }

  /**
   * Login using email and password
   * @param formValue Value of email and password
   * @returns Observable
   */
  public login(formValue: Credentials): Observable<AuthData> {
    return this.http
      .post<AuthDataResponse>(`${environment.v2.baseUrl}auth/sign-in`, formValue, {
        context: new HttpContext().set(BypassErrorToast, true).set(BypassErrorHandling, true),
        headers: new HttpHeaders({
          'X-Device-Name': this._getDeviceName()
        })
      })
      .pipe(
        map((response: AuthDataResponse) => {
          this.user = response.data
          return response.data
        })
      )
  }

  /**
   * Login using a provided token
   * @param token user token
   * @returns Observable
   */
  public loginWithToken(token: string): Observable<any> {
    return this.http.get(`${environment.planningApi}auth`, {
      headers: new HttpHeaders({
        token: token
      })
    })
  }

  /**
   * Sends a reset password email request
   * @param formValue email to reset password
   * @returns Observable
   */
  confirmEmail(formValue: any) {
    return this.http.post(`${environment.planningApi}auth/resetPassword/email`, formValue)
  }

  /**
   * Confirms a registered email address
   * @param email email to confirm
   * @returns Observable
   */
  confirmRegisteredEmail(email: string) {
    return this.http.post(`${environment.planningApi}auth/confirm/email`, { email })
  }

  /**
   * Gets a list of all workspaces assigned to a user
   * @returns Observable
   */
  public getWorkspaces() {
    return this.http.get(`${environment.planningApi}workspaces`).pipe(tap((workspaces: Response<Workspace[]>) => (this.workspaces = workspaces.data)))
  }

  /**
   * Gets the last used workspace ID from the backend
   * @returns Observable<number | null>
   */
  public getLastUsedWorkspace(): Observable<number | null> {
    if (this.isAdmin) return of(null)
    return this.http
      .get<{ organization_id: number }>(`${environment.v2.baseUrl}plan/contactless/organizations/last-active`, {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      })
      .pipe(
        map((response) => response.organization_id),
        catchError(() => {
          this.currentWorkspace = this.workspaces[0]
          return of(null)
        })
      )
  }
  /**
   * Updates user details
   * @param formValue user updated information
   * @returns Observable
   */
  public updateUser(formValue: any): Observable<any> {
    return this.http.patch(`${environment.planningApi}auth/`, formValue)
  }

  /**
   * Updates user options metadata
   * @param option options metadata
   * @returns Observable
   */
  public updateUserOptions(option) {
    return this.http.patch(`${environment.planningApi}auth/`, { options: { display_tutorial: option } })
  }

  /**
   * Gets a list of all available plans
   * @returns Observable
   */
  public getPlans(): Observable<any> {
    return this.http.get(`${environment.planningApi}plans`)
  }

  /**
   * Gets a list of all available plans with it's prices objects
   * @returns Observable
   */
  public getPlansWithPrices() {
    return this.http.get<PlanPrice[]>(`${environment.planningApi}plans/prices`)
  }

  /**
   * Redirects the user to a stripe payment session
   * @param eventId event id
   * @param priceId price id
   * @param quantity quantity
   * @param success_url success url callback
   * @param cancel_url cancel url callback
   * @deprecated
   * @returns Observable
   */
  public postSession(eventId: number, priceId: string, quantity: number, success_url: string, cancel_url: string) {
    return this.http.post(`${environment.planningApi}sessions`, {
      event_id: eventId,
      price_id: priceId,
      quantity: quantity,
      success_url: success_url,
      cancel_url: cancel_url
    })
  }

  /**
   * Gets an organization info or if none exists returns an empty organization info object @see LegalInfo
   * @returns Observable
   */
  public getOrganizationInfo(workspace: Workspace = this.currentParentWorkspace): Observable<LegalInfo> {
    return this.http.get<LegalInfo>(`${environment.planningApi}workspaces/legal-info?workspace_id=${workspace.id}`).pipe(
      catchError(() => {
        return of({
          company_address: '',
          company_description: '',
          company_email: '',
          company_name: '',
          company_phone: '',
          company_type: null,
          country: '',
          workspace_id: workspace.id,
          industry_sector_id: null
        })
      })
    )
  }
  /**
   * Updates an organization profile
   * @param data LegalInfo @see LegalInfo
   * @returns Observable
   */
  public updateOrganization(data: any) {
    return this.http.patch(`${environment.planningApi}workspaces/legal-info`, data, {
      context: new HttpContext().set(BypassErrorToast, true).set(BypassErrorHandling, true)
    })
  }
  /**
   * Redirects the user to stripe to confirm his subscription
   * @param priceId product price id
   * @returns Observable
   */
  public createSubscription(priceId) {
    return this.http.post(`${environment.planningApi}sessions/subscription`, {
      workspace_id: this.currentParentWorkspace?.id ?? this.currentWorkspace?.id,
      price_id: priceId,
      return_url: `${environment.baseUrl}subscription/success`
    })
  }

  /**
   * Checks if the current user is eligible to create a subscription for the current organization/workspace.
   * Calls the backend endpoint to verify the user's eligibility.
   * @returns Observable<boolean> - true if eligible, false otherwise
   */
  public canUpgradeOrganization(): Observable<boolean> {
    return this.http
      .post<{ isEligibleForSubscribe: boolean }>(`${environment.planningApi}sessions/check-role`, {
        user_id: this.user?.id,
        organization_id: this.currentParentWorkspace?.id ?? this.currentWorkspace?.id
      })
      .pipe(map((result: { isEligibleForSubscribe: boolean }) => result.isEligibleForSubscribe))
  }
  /**
   * Determines whether the user has any billing history
   * @deprecated
   * @returns Observable
   */
  public hasBillingHistory(): Observable<boolean> {
    const id = this.currentParentWorkspace?.id ?? this.currentWorkspace?.id
    if (!id) {
      return of(false)
    }
    return this.http.get(`${environment.planningApi}invoices/hasAny?workspace_id=${id}`).pipe(map((result: any) => result.hasAnyInvoices))
  }
  /**
   * Redirects the user to it's Stripe customer portal
   * @param return_url return url @deprecated
   * @returns Observable
   */
  public createCustomerPortal(return_url) {
    const billingPath = this.getOrganizationBillingUrl()
    return this.http.post(`${environment.planningApi}sessions/customerPortal`, {
      workspace_id: this.currentParentWorkspace?.id ?? this.currentWorkspace?.id,
      return_url: `${environment.baseUrl}${billingPath}`
    })
  }
  /**
   * Gets the user token
   * @returns string
   */
  public getToken() {
    return this._token
  }

  private fetchBffToken(): Promise<{ access_token: string; expires_at: number } | null> {
    return fetch(`${environment.baseContactlessApi}v3/auth/bff/token`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return null
        const json = (await res.json()) as any
        if (!json?.access_token) return null
        return { access_token: String(json.access_token), expires_at: Number(json.expires_at) || 0 }
      })
      .catch(() => null)
  }

  /**
   * Get a valid access token (in-memory only). Refresh happens server-side.
   */
  public getAccessToken$(opts?: { force?: boolean }): Observable<string | null> {
    const force = Boolean(opts?.force)
    const nowSec = Math.floor(Date.now() / 1000)
    if (!force && this._token && this._tokenExpiresAtSec && this._tokenExpiresAtSec > nowSec + 30) {
      return of(this._token)
    }
    if (!force && this._tokenInFlight$) return this._tokenInFlight$

    this._tokenInFlight$ = from(this.fetchBffToken()).pipe(
      map((t) => {
        if (!t?.access_token) return null
        this._token = t.access_token
        this._tokenExpiresAtSec = t.expires_at || null
        return this._token
      }),
      finalize(() => {
        this._tokenInFlight$ = undefined
      }),
      shareReplay(1)
    )
    return this._tokenInFlight$
  }

  /**
   * Deremintes whether the user is loggend in or not
   * @returns boolean
   */
  public get isLoggedIn() {
    return this._token != null
  }

  /**
   * Removes currently stored user
   */
  public clearUser() {
    this._token = null
    this._tokenExpiresAtSec = null
    this._tokenInFlight$ = undefined
    this.user = null
  }

  /**
   * Gets the current user role
   * @returns string
   */
  get role(): 'Admin' | 'Main Entity' | 'Sub Entity' {
    if (!this.user && !this.currentWorkspace) {
      return null
    }
    if (this.isAdmin) {
      return 'Admin'
    }
    return this.currentWorkspace?.parent && this.currentWorkspace.parent_workspace?.id ? 'Sub Entity' : 'Main Entity'
  }
  /**
   * Gets the current user plan
   * @returns string | null
   */
  get plan(): 'Basic' | 'Pro' | '' | null {
    if (!this.user && !this.currentWorkspace) {
      return null
    }
    const planId = this.currentWorkspace?.parent_workspace ? this.currentWorkspace?.parent_workspace.plan_id : this.currentWorkspace?.plan_id

    switch (planId) {
      case 2:
        return 'Pro'
      case 1:
        return 'Basic'
      default:
        return ''
    }
  }

  /** Fallback unit prices until plan/workspace APIs expose per-channel notification rates. */
  private readonly DEFAULT_RESEND_CONFIRMATION_UNIT_PRICE_USD: Record<NotificationChannel, number> = {
    email: 0.02,
    sms: 0.28,
    whatsapp: 0.06
  }

  private readonly DEFAULT_INVITATION_UNIT_PRICE_USD: Record<NotificationChannel, number> = {
    email: 0.02,
    sms: 0.28,
    whatsapp: 0.4
  }
  private readonly DEFAULT_IMPORT_CONFIRMATION_UNIT_PRICE_USD: Record<NotificationChannel, number> = {
    email: 0,
    sms: 0.28,
    whatsapp: 0.06
  }
  /** USD per import confirmation message by channel (product pricing). */
  /** @returns USD per import confirmation message by channel (product pricing). */
  get importConfirmationUnitPriceUsd(): Readonly<Record<NotificationChannel, number>> {
    return this._resolveImportConfirmationUnitPriceUsd(this.billingWorkspace)
  }
  /** @internal Resolves the import confirmation unit price USD for the given workspace. */
  private _resolveImportConfirmationUnitPriceUsd(_workspace: Workspace | null): Readonly<Record<NotificationChannel, number>> {
    return this.DEFAULT_IMPORT_CONFIRMATION_UNIT_PRICE_USD
  }
  /** Workspace that owns billing and plan pricing (parent org when present). */
  private get billingWorkspace(): Workspace | null {
    return this.currentParentWorkspace ?? null
  }

  /** USD per resend-confirmation message by channel (product pricing). */
  get resendConfirmationUnitPriceUsd(): Readonly<Record<NotificationChannel, number>> {
    return this._resolveResendConfirmationUnitPriceUsd(this.billingWorkspace)
  }

  /** USD per invitation message by channel (product pricing). */
  get invitationUnitPriceUsd(): Readonly<Record<NotificationChannel, number>> {
    return this._resolveInvitationUnitPriceUsd(this.billingWorkspace)
  }

  calculateConfirmationTotalPriceUsd(channel: NotificationChannel, count: number): number {
    return this.resendConfirmationUnitPriceUsd[channel] * count
  }

  calculateInvitationTotalPriceUsd(channel: NotificationChannel, count: number): number {
    return this.invitationUnitPriceUsd[channel] * count
  }

  private _resolveResendConfirmationUnitPriceUsd(_workspace: Workspace | null): Readonly<Record<NotificationChannel, number>> {
    // TODO: map plan prices from workspace when API exposes per-channel notification rates
    return this.DEFAULT_RESEND_CONFIRMATION_UNIT_PRICE_USD
  }

  private _resolveInvitationUnitPriceUsd(_workspace: Workspace | null): Readonly<Record<NotificationChannel, number>> {
    // TODO: map plan prices from workspace when API exposes per-channel notification rates
    return this.DEFAULT_INVITATION_UNIT_PRICE_USD
  }

  /**
   * Signs up the user to the planning system
   * @param form user registration details
   * @returns Observable
   */
  public signup(formValue: Credentials): Observable<AuthDataResponse> {
    return this.http.post<AuthDataResponse>(`${environment.v2.baseUrl}auth/sign-up`, formValue, {
      context: new HttpContext().set(BypassErrorToast, true),
      headers: new HttpHeaders({
        'X-Device-Name': this._getDeviceName()
      })
    })
  }

  /**
   * @readonly Gets the user token from the local storage or session storage
   * @returns string
   */
  public get token(): string | null {
    return this._token
  }

  /**
   * Checks if an email is already registered with MICEtribe
   * @param email email address to check
   * @returns Observable
   */
  public checkIfEmailRegistered(email: string): Observable<EmailExistsResponse> {
    return this.http.post<EmailExistsResponse>(
      `${environment.v2.baseUrl}auth/email-exists`,
      { email },
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  public saveUserCredentials(data: AuthData, shouldNavigate: boolean = true): void {
    // Reuse the setter to ensure consistent V4->V3 transformation and BehaviorSubject updates
    this.user = data as any
  }

  // Silent auth + code exchange are handled server-side in BFF mode.

  /**
   * Fetches the current user's data including feature flags from /auth/me
   * Call this after login or signup to load user flags
   * @returns Observable with user data including flags
   */
  public getUserData(): Observable<any> {
    return this.http.get(`${environment.v2.baseUrl}auth/me`).pipe(
      tap((response: any) => {
        this.flags = response?.data?.user?.flags ?? []
        if (response?.data?.user) {
          this.user = response.data.user
        }
      }),
      catchError(() => of(null))
    )
  }

  public signOutWithoutSubscription(redirectUrl?: string | null): void {
    if (this._signOutInProgress) return
    this._signOutInProgress = true

    const planRedirectUrl = redirectUrl && this._isPlanReturnUrl(redirectUrl) ? redirectUrl : null
    if (planRedirectUrl) {
      this.setRedirectUrl(planRedirectUrl)
    }

    // Security: clear all app persisted state to prevent cross-user data bleed.
    // Preserve only purely-UI preferences.
    const preserved: Record<string, string> = {}
    ;['theme', 'langCode'].forEach((k) => {
      try {
        const v = localStorage.getItem(k)
        if (v !== null) preserved[k] = v
      } catch {}
    })
    try {
      localStorage.clear()
    } catch {}
    try {
      sessionStorage.clear()
    } catch {}
    Object.entries(preserved).forEach(([k, v]) => {
      try {
        localStorage.setItem(k, v)
      } catch {}
    })
    // Keep redirect if explicitly provided above.
    if (planRedirectUrl) {
      try {
        localStorage.setItem('redirect_url', planRedirectUrl)
      } catch {}
    }

    this.user = null
    this.currentWorkspace = null
    this.currentParentWorkspace = null
    this.workspaces = []
    this.plans = []
    this._token = null
    this._tokenExpiresAtSec = null
    this._tokenInFlight$ = undefined
    this.userRoleInCurrentWorksapce.next(null)
    this.currentWorkspaceChange.next(null)
    this.currentParentWorkspaceChange.next(null)
    this._organizationService.organizationNameWhileEditing.set(null)

    // Global logout must clear the IdP web session cookie too.
    // Use a full-page navigation to the IdP logout endpoint so cookies are always included.
    const authUiUrl = (environment as any).authUiUrl as string | undefined
    const returnTo = encodeURIComponent(authUiUrl ?? '')
    window.location.replace(`${environment.v2.baseUrl}auth/sign-out?return_to=${returnTo}`)
  }
  public redirectToAuth(): void {
    void this.beginLogin(window.location.pathname + window.location.search + window.location.hash)
  }
  public signOut(): Observable<StatusResponse> {
    this.signOutWithoutSubscription()
    // Usually not reached because of navigation.
    return of({ data: [], message: 'Signing out...' } as any)
  }

  /**
   * Confirms an email address by a token sent to it's inbox
   * @param token string
   * @returns Observable
   */
  public validateEmailConfirmation(token: string) {
    return this.http.get(`${environment.planningApi}auth/confirm?token=${token}`)
  }

  /**
   * Get a list of available Industry Sectors
   * @returns Observable
   */
  getIndustrySectors(): Observable<any> {
    return this.http.get(`${environment.industrySector}`)
  }

  /**
   * Confirm whether all Facebook data has been deleted
   * @param userId
   * @returns Observable
   */
  public deleteFacebookDataConfirmation(userId: number): Observable<any> {
    return this.http.get(`${environment.planningApi}auth/login/facebook/delete/confirm?id=${userId}`)
  }

  /**
   * @public
   * @param {IRunningJob[]} jobs - The new running jobs.
   */
  updateRunningJobs(jobs: IRunningJob[]) {
    this.runningJobsSource.next(jobs)
  }

  /**
   * @description Signs in the user using the given provider and access token
   * @param provider accepts 'google' | 'linkedin'
   * @param accessToken accepts <access_token> for 'google' and <code> for 'linkedin'
   * @returns Observable<AuthData>
   */
  public signInWith(provider: 'google' | 'linkedin', accessToken: string): Observable<AuthDataResponse> {
    const payload = provider === 'linkedin' ? { code: accessToken, remember_me: true } : { access_token: accessToken, remember_me: true }
    return this.http.post<AuthDataResponse>(`${environment.v2.baseUrl}auth/sign-in/${provider}`, payload).pipe(tap((response) => (this.user = response.data)))
  }

  /**
   * @description Handles Google Login button click event and init token client to login the user with google
   * @param handler callback function to handle the response from google
   * @returns void
   */
  public signInWithGoogle(handler: Function): void {
    const client = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: environment.googleClientID,
      scope: 'openid profile email https://www.googleapis.com/auth/user.phonenumbers.read https://www.googleapis.com/auth/user.organization.read',
      auto_select: true,
      callback: handler
    })

    client.requestAccessToken()
  }

  /**
   * @description Grants the user access to send emails using Google
   * @param email The email address that should be automatically selected
   * @param handler Callback function to handle the response from Google
   * @returns void
   */
  public grantEmailSendAccess(email: string, handler: Function): void {
    const client = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: environment.googleClientID,
      scope: 'https://www.googleapis.com/auth/gmail.send',
      auto_select: true,
      login_hint: email,
      callback: handler
    })

    client.requestAccessToken()
  }

  /**
   * @description Injects the Google Auth library script to the head of the document if it's not already there
   * @returns void
   */
  public injectGoogleScript(): void {
    if (document.getElementById('google-auth-script')) return

    const script = document.createElement('script')
    script.id = 'google-auth-script'
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    document.head.appendChild(script)
  }

  /**
   * @TODO: Implement Unit test
   * @description Handles LinkedIn Login button click event and opens a new window to login the user with LinkedIn
   * @returns void
   */
  public signInWithLinkedIn(): void {
    const url = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${environment.linkedinClientID}&redirect_uri=${location.href}&scope=r_liteprofile%20r_emailaddress&prompt=login`
    location.href = url
  }

  /**
   * @description Sends a reset password email request to the given email address
   * @param {{email: string}} formValue - The form data used for the request body
   * @returns Observable<StatusResponse>
   */
  public forgotPassword(formValue: { email: string }): Observable<Response<ForgetPasswordResponse>> {
    return this.http.post<Response<ForgetPasswordResponse>>(`${environment.v2.baseUrl}auth/forget-password`, formValue, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * @description Resets the password of the user using the given token
   * @param {{token: string, newPassword: string, newPasswordConfirmation: string}} formValue - The form data used for the request body
   * @returns Observable<StatusResponse>
   */
  public resetPassword(formValue: { token: string; newPassword: string; newPasswordConfirmation: string }): Observable<StatusResponse> {
    return this.http.post<StatusResponse>(`${environment.v2.baseUrl}auth/reset-password`, formValue, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  public checkIfTokenValid(email: string, token: string): Observable<Response<{ is_token_valid: boolean }>> {
    return this.http.post<Response<{ is_token_valid: boolean }>>(
      `${environment.v2.baseUrl}auth/check-reset-password-token`,
      {
        email,
        token
      },
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  // return the user's profile picture
  public get avatar() {
    return this.user?.profile_picture
  }

  /**
   * Centralized "is admin" check.
   * Some APIs return `is_admin` as `1`/`0` or `true`/`false`.
   */
  public get isAdmin(): boolean {
    return Boolean((this.user as any)?.is_admin)
  }

  /**
   * Centralized user email accessor (never throws).
   */
  public get userEmail(): string {
    return (this.user as any)?.email ?? ''
  }

  /**
   * Centralized user display name accessor (used across UI and audit fields).
   * Falls back to email if no full name is present.
   */
  public get userDisplayName(): string {
    const u: any = this.user ?? {}
    return (u.full_name || u.name || u.email || '').toString()
  }

  /**
   * Best-effort stable user identifier for audit fields.
   * (Some endpoints used to return `uuid`; if missing we fall back to `id`.)
   */
  public get userUuid(): string | undefined {
    const u: any = this.user ?? {}
    return u.uuid ? String(u.uuid) : u.id !== undefined && u.id !== null ? String(u.id) : undefined
  }

  /**
   * Convenience payload for "approved by" fields in registrations.
   */
  public get approvedByPlanningPayload(): { name: string; uuid?: string; source: 'planning' } | null {
    const name = this.userDisplayName
    if (!name) return null
    return { name, uuid: this.userUuid, source: 'planning' }
  }

  // return the first and last initial of the user's full name, if not available, return the first initial of the user's email
  public get userInitials() {
    const fullName = this.user?.full_name?.split(' ')
    const fullNameInitials = fullName?.length > 1 ? fullName[0][0] + fullName[1][0] : undefined
    const emailInitials = this.user?.email[0].toUpperCase()
    return fullNameInitials?.toUpperCase() || emailInitials || ''
  }

  /**
   * Organization ID used for billing / organization-settings routes (parent org when on a sub-entity).
   * @param {Workspace} [workspace] Workspace to resolve from; defaults to the current workspace
   * @returns {number | string | null} Organization workspace ID when available
   */
  public getBillingOrganizationId(workspace: Workspace = this.currentWorkspace): number | string | null {
    const source = workspace ?? this.currentParentWorkspace
    if (!source) return null

    if (source.parent) {
      return source.parent_workspace?.id ?? source.parent
    }

    return source.id
  }

  /**
   * Path to the organization settings billing section for the given (or current) workspace.
   * @param {Workspace} [workspace] Workspace to resolve from; defaults to the current workspace
   * @returns {string | null} Router path including the billing fragment, or null when no workspace is set
   */
  public getOrganizationBillingUrl(workspace: Workspace = this.currentWorkspace): string | null {
    const organizationId = this.getBillingOrganizationId(workspace)
    if (!organizationId) return '/workspaces/list'

    return `/workspaces/${organizationId}/organization-settings#billing`
  }

  /**
   * Open organization settings billing in a new browser tab.
   * Used when an in-app modal (e.g. settle) must stay open on the current page.
   * @param {Workspace} [workspace] Workspace to resolve billing org from
   */
  public openOrganizationBillingInNewTab(workspace: Workspace = this.currentWorkspace): void {
    const billingUrl = this.getOrganizationBillingUrl(workspace)

    if (billingUrl && billingUrl !== '/workspaces/list') {
      window.open(billingUrl, '_blank')
      return
    }

    this.navigateToPage('billing')
  }

  public navigateToPage(target: 'billing' | 'admin-tools' | 'profile' | 'home'): void {
    if (target === 'profile') {
      this._router.navigate(['app-settings/profile'])
      return
    }
    if (target === 'home') {
      this._router.navigate(['workspaces/list'])
      return
    }

    const parentWorkspaces: Workspace[] = this.workspaces.filter((workspace) => workspace.parent === null)
    const message = target === 'billing' ? 'Please set your workspace first' : 'Please select a workspace first'

    const navigateBillingOrAdmin = (workspace: Workspace) => {
      if (target === 'billing') {
        const billingUrl = this.getOrganizationBillingUrl(workspace)
        if (billingUrl && billingUrl !== '/workspaces/list') {
          this._router.navigateByUrl(billingUrl)
        }
        return
      }

      this._router.navigate(['/admin/tools'])
    }

    if (this.currentWorkspace && (this.isAdmin || this.currentParentWorkspace)) {
      navigateBillingOrAdmin(this.currentWorkspace)
    } else if (parentWorkspaces.length === 1) {
      this.currentParentWorkspace = parentWorkspaces[0]
      this.currentWorkspace = parentWorkspaces[0]
      navigateBillingOrAdmin(parentWorkspaces[0])
    } else {
      this._alertService.alert(Alert.INFO, message, 5000)
    }
  }

  setCurrentSystem(system: SystemOptions): void {
    localStorage.setItem('currentSystem', JSON.stringify(system))
  }

  /**
   * Cross-app redirect URL helpers.
   * Components should not touch storage directly.
   */
  public setRedirectUrl(url: string): void {
    localStorage.setItem('redirect_url', url)
    // Backward-compatible key
    localStorage.setItem('redirect', url)
  }

  public clearRedirectUrl(): void {
    localStorage.removeItem('redirect_url')
    localStorage.removeItem('redirect')
  }

  public getRedirectUrl(): string | null {
    return localStorage.getItem('redirect_url') || localStorage.getItem('redirect')
  }

  /**
   * Admin tools UI state (workspace switcher).
   */
  public setAdminToolsActiveView(view: string): void {
    localStorage.setItem('adminToolsActiveView', view)
  }

  public getAdminToolsActiveView(): string | null {
    return localStorage.getItem('adminToolsActiveView')
  }

  public navigateToCrew(): void {
    window.open(`${environment.crewSystem}`, '_blank')
  }

  /**
   * Trims the user's full name if it's longer than 25 characters
   * @param name The name to trim
   * @returns The trimmed name with ellipsis if needed
   */
  public trimUserName(name: string): string {
    if (!name) return ''
    return name.length > 15 ? name.substring(0, 15) + '...' : name
  }

  /**
   * @description Sets the current workspace and navigates to events list, or org-settings billing for accountants.
   * @param workspace Workspace that was selected
   */
  private _updateAndNavigateToEventsList(workspace: Workspace): void {
    this.currentWorkspace = workspace as Workspace
    // Handle parent workspace logic
    // If selected workspace has a parent, set the parent workspace.
    // If not, set the workspace as both current and parent workspace.
    if (workspace.parent_workspace) this.currentParentWorkspace = workspace.parent_workspace as Workspace
    else this.currentParentWorkspace = workspace as Workspace

    this.navigateToWorkspaceLanding()
  }

  /**
   * Navigate to events list, or organization settings billing when the user is an accountant in the current workspace.
   * Re-syncs role first so switches between orgs always use the role for the newly selected org.
   */
  public navigateToWorkspaceLanding(): void {
    const workspaceRole = this._injector.get(WorkspaceRoleService)
    workspaceRole.syncRoleFromCurrentWorkspace()

    if (!this.isAdmin && workspaceRole.isAccountant()) {
      const billingUrl = this.getOrganizationBillingUrl()
      this._router.navigateByUrl(billingUrl)
      return
    }

    this._router.navigateByUrl('/events/list')
  }

  /**
   * Returns the plan origin
   * @returns The plan origin
   */
  private _planOrigin(): string {
    return window.location.origin.replace(/\/+$/, '')
  }

  /**
   * Checks if the given URL is a valid plan return URL
   * @param url The URL to check
   * @returns True if the URL is a valid plan return URL, false otherwise
   */
  private _isPlanReturnUrl(url: string): boolean {
    try {
      return new URL(url, this._planOrigin()).origin === this._planOrigin()
    } catch {
      return false
    }
  }

  /**
   * Resolves the plan return to URL
   * @param intendedUrl The intended URL to resolve
   * @returns The resolved plan return to URL
   */
  private _resolvePlanReturnTo(intendedUrl?: string): string {
    const planOrigin = this._planOrigin()
    const defaultReturn = `${planOrigin}/`
    const raw = (intendedUrl || '').trim()

    let candidate: string
    if (!raw) candidate = window.location.href
    else if (raw.startsWith('http://') || raw.startsWith('https://')) candidate = raw
    else candidate = planOrigin + (raw.startsWith('/') ? raw : `/${raw}`)

    try {
      const url = new URL(candidate, planOrigin)
      if (url.origin !== planOrigin) return defaultReturn
      ;['redirect', 'redirect_url', 'mt_token'].forEach((key) => url.searchParams.delete(key))
      const nestedReturnTo = url.searchParams.get('return_to')
      if (nestedReturnTo && !this._isPlanReturnUrl(nestedReturnTo)) {
        url.searchParams.delete('return_to')
      }
      return url.toString()
    } catch {
      return defaultReturn
    }
  }

  /**
   * Updates the last active organization for the current user
   * Only updates localStorage if API returns 200 status
   * @param workspace The workspace to mark as last active
   * @returns Observable
   */
  public updateLastActiveOrganization(workspace: Workspace) {
    // If the user is admin, set the localstorage and navigate to events list
    if (this.isAdmin) {
      this._updateAndNavigateToEventsList(workspace)
      return of(null)
    }
    return this.http.post(`${environment.v2.baseUrl}plan/contactless/organizations/${workspace.id}/last-active`, {}).pipe(
      tap(() => {
        // Only update localStorage if API call succeeds (200 status)
        this._updateAndNavigateToEventsList(workspace)
      })
    )
  }
}
